import React, { memo, useState, useRef, useEffect } from 'react';
import { Hr } from 'components/atoms/Hr';
import {
	PayPalButtons,
	PayPalHostedFieldsProvider,
	PayPalHostedField,
	PAYPAL_HOSTED_FIELDS_TYPES,
	FUNDING,
} from '@paypal/react-paypal-js';
import {
	callCreateBulkOrderApi,
	callGenerateBillingAgreementTokenApi,
	callGenerateBillingAgreementIdApi,
} from 'requests/RequestPayment';
import { Payment, isPayPal, isCredit, OrderTypeEntity, Page } from 'consts/BulkPaymentConst';
import { HttpStatus } from 'consts/HttpStatus';
import { CARD_MAX_MESSAGE } from 'consts/CardConst';
import { RoutePath } from 'consts/RoutePath';
import { CircularProgress, Backdrop } from '@material-ui/core';
import { useReceiveBulkPaymentRequest } from 'hooks/bulk-payment/UseReceiveBulkPaymentRequest';
import { useBulkCreditCardForm } from 'hooks/bulk-payment/UseBulkCreditCardForm';
import { useBulkPeriodicPaymentOverview } from 'hooks/bulk-payment/UseBulkPeriodicPaymentOverView';
import { useFraudNet } from 'hooks/payment/UseFraudNet';
import { useBulkPaymentPage } from 'hooks/bulk-payment/UseBulkPaymentPage';
import { useBulkPaymentCards } from 'hooks/payment/UseBulkPaymentCards';
import { useAgreeSendingCardInfo } from 'hooks/UseAgreeSendingCardInfo';
import { ErrorTemplate } from './ErrorTemplate';
import { UnSupportedTemplate } from './UnSupportedTemplate';
import { ConfirmComponent } from 'components/organisms/BulkPaymentConfirm';
import { AgreeSendingCardInfo } from 'components/molecules/agree-sending-card-info/AgreeSendingCardInfo';
import classes from './override.module.scss';

type Props = {};

export const BulkPeriodicPaymentTemplate = memo((_props: Props) => {
	const scrollRef = useRef(null);
	const scrollTop = () => {
		scrollRef.current.scrollIntoView();
	};
	const { isAgreeSendCardInfo, handleChangeAgreeCheckValue } = useAgreeSendingCardInfo();
	const { page, setPage, isDefault, isShowSavedCard, isDeleteCard, isConfirm, isUnSupported, isServiceError } =
		useBulkPaymentPage();
	const {
		getOrderLoading,
		order,
		redirect_uri,
		state,
		error,
		setError,
		resetOptions,
		transErrorPage,
		siteSettlement,
		site,
		is_via_portal,
	} = useReceiveBulkPaymentRequest(OrderTypeEntity.PERIODIC_PAYMENT, setPage);
	const { f } = useFraudNet(order?.merchant_id);
	const {
		isSaveCard,
		payMethod,
		paymentTokenExists,
		handleChangeRadioValue,
		displayCardBrandImage,
		submitLoading,
		setSubmitLoading,
		SubmitPayment,
		confirmLoading,
		setConfirmLoading,
		redirect,
		monthSelectList,
		isPaymentTokenMax,
		cardError,
		setCardError,
		setPageConfirm,
	} = useBulkCreditCardForm(
		order,
		transErrorPage,
		redirect_uri,
		state,
		is_via_portal,
		OrderTypeEntity.PERIODIC_PAYMENT,
		resetOptions,
		setPage,
		setError,
		scrollTop,
		isAgreeSendCardInfo,
		true,
	);
	const { BulkPeriodicPaymentOverview } = useBulkPeriodicPaymentOverview(order);
	const {
		NEW_CARD_SELECT_VALUE,
		handleChangeSavedCardSelectValue,
		selectedCard,
		isCardSelected,
		selectDeleteCard,
		deleteCardLoading,
		DeleteCardComponent,
	} = useBulkPaymentCards(Page, setPage, error, setError, order, displayCardBrandImage);

	// PayPalボタン用 定期購読トークン作成
	const createBillingAgreementToken = async () => {
		const createBillingAgreementTokenResult = await callGenerateBillingAgreementTokenApi();
		if (createBillingAgreementTokenResult.status !== HttpStatus.OK) {
			transErrorPage();
			setSubmitLoading(false);
			return;
		}
		return createBillingAgreementTokenResult.data.token_id;
	};

	// PayPalボタン用 定期購読ID作成
	const onApprove = async (data) => {
		const createBillingAgreementIdResult = await callGenerateBillingAgreementIdApi({
			token_id: data.billingToken,
			// gid_order_no: order.gid_order_no,
		});
		if (createBillingAgreementIdResult.status !== HttpStatus.OK) {
			transErrorPage();
			return;
		}

		const createOrderResult = await callCreateBulkOrderApi({
			gid_order_no: order.gid_order_no,
			agreement_id: createBillingAgreementIdResult.data.agreement_id,
			payment_type: Payment.PAYPAL.key,
		});
		if (createOrderResult.status !== HttpStatus.OK) {
			transErrorPage();
			return;
		}
		setPageConfirm();
	};

	// クレジットカード用 決済情報作成
	const createOrder = async () => {
		const createOrderResult = await callCreateBulkOrderApi({
			gid_order_no: order.gid_order_no,
			payment_type: Payment.CREDIT.key,
		});
		if (createOrderResult.status !== HttpStatus.OK) {
			transErrorPage();
			return;
		}
		return createOrderResult.data.paypal_order_id;
	};

	const [processLoading, setProcessLoading] = useState(false);
	// 保存済みクレジットカードで決済する
	const paySavedCreditCard = async () => {
		setProcessLoading(true);
		const createOrderResult = await callCreateBulkOrderApi({
			gid_order_no: order.gid_order_no,
			paypal_client_metadata_id: f,
			payment_tokens_id: order.payment_tokens[selectedCard].payment_id,
			payment_type: Payment.CREDIT.key,
		});
		if (createOrderResult.status !== HttpStatus.OK) {
			transErrorPage();
			setProcessLoading(false);
			return;
		}
		setProcessLoading(false);
		setPageConfirm();
	};

	useEffect(() => {
		if (isServiceError() || isUnSupported()) {
			return;
		}
		setError('');
		scrollTop();
		document.title = 'お支払い方法の選択';
	}, [page]);

	return (
		<>
			<Backdrop
				className={classes.backdrop}
				open={getOrderLoading || submitLoading || processLoading || deleteCardLoading || confirmLoading}
			>
				<CircularProgress color="inherit" />
			</Backdrop>
			{isServiceError() && (
				<ErrorTemplate
					button_display={siteSettlement.error_button_display}
					return_url={siteSettlement.error_return_url}
				/>
			)}

			{isUnSupported() && (
				<UnSupportedTemplate
					button_display={siteSettlement.error_button_display}
					return_url={siteSettlement.error_return_url}
				/>
			)}

			{!isServiceError() && !isUnSupported() && (
				<div className="section mypage" ref={scrollRef}>
					{getOrderLoading ? (
						<div>注文情報取得中</div>
					) : (
						<>
							<div className="section-in" style={{ marginTop: 50 }}>
								{isDefault() && (
									<>
										<BulkPeriodicPaymentOverview cardError={cardError} error={error} />
										<div className={`info-box form-box ${classes.paymentSelectWrap}`}>
											<div className="form-input">
												{/* PayPal非表示フラグがOFFの場合表示 */}
												{order.payment_specify_list?.paypal_hidden_flg !== 1 && (
													<>
														<div className={classes.sectionPaymentInfoRow}>
															<div className="radio-box">
																<ul>
																	<li>
																		<label
																			className={`radio-label ${classes.paymentMethodLabel} ${
																				isPayPal(payMethod) ? 'is-active' : ''
																			}`}
																			htmlFor="paypal"
																		>
																			<input
																				type="radio"
																				id="paypal"
																				name="method"
																				value={Payment.PAYPAL.key}
																				checked={isPayPal(payMethod)}
																				onChange={handleChangeRadioValue}
																			/>
																			PayPal決済
																		</label>
																	</li>
																</ul>
															</div>
															<div className={classes.paymentPaypalInfoText}>
																ペイパルのアカウントをお持ちであればIDとパスワードだけで決済が完了します
															</div>
															{isPayPal(payMethod) && (
																<PayPalButtons
																	// PayPalボタンのみ表示
																	fundingSource={FUNDING.PAYPAL}
																	style={{ label: 'pay' }}
																	// PayPalボタン押下時に発火
																	createBillingAgreement={createBillingAgreementToken}
																	// PayPalポップアップ閉じた後に発火
																	onApprove={(data) => onApprove(data)}
																	onError={(error) => {
																		transErrorPage();
																		console.error(`error: ${JSON.stringify(error)}`);
																	}}
																/>
															)}
														</div>
														<Hr />
													</>
												)}
												{/* クレジットカード非表示フラグがOFFの場合表示 */}
												{order.payment_specify_list?.credit_hidden_flg !== 1 && (
													<>
														<div className={classes.sectionPaymentInfoRow}>
															<div className={classes.paymentCreditHead}>
																<div className="radio-box">
																	<ul>
																		<li>
																			<label
																				className={`radio-label ${classes.paymentMethodLabel} ${
																					isCredit(payMethod) ? 'is-active' : ''
																				}`}
																				htmlFor="credit"
																			>
																				<input
																					type="radio"
																					id="credit"
																					name="method"
																					value={Payment.CREDIT.key}
																					checked={isCredit(payMethod)}
																					onChange={handleChangeRadioValue}
																				/>
																				クレジットカード
																			</label>
																		</li>
																	</ul>
																</div>
																<div className={`${classes.paymentCreditCardBox} ${classes.flexCenter}`}>
																	{isCredit(payMethod) && paymentTokenExists && !isCardSelected(NEW_CARD_SELECT_VALUE)
																		? displayCardBrandImage(order.payment_tokens[selectedCard].brand)
																		: displayCardBrandImage()}
																</div>
															</div>
															{/* 保存済みカード情報IDがないor新規カードが選択されている場合は入力フォーム */}
															{isCredit(payMethod) && (!paymentTokenExists || isCardSelected(NEW_CARD_SELECT_VALUE)) && (
																<PayPalHostedFieldsProvider
																	// 次へ押下時にsubmitの前に発火
																	createOrder={createOrder}
																	// 資格情報が無い場合に発火
																	notEligibleError={<div>Your account is not eligible</div>}
																>
																	<PayPalHostedField
																		hostedFieldType={PAYPAL_HOSTED_FIELDS_TYPES.NUMBER}
																		options={{ selector: '#card-number', placeholder: '********' }}
																	/>
																	<PayPalHostedField
																		hostedFieldType={PAYPAL_HOSTED_FIELDS_TYPES.EXPIRATION_MONTH}
																		options={{
																			selector: '#expiration-month',
																			placeholder: 'MM',
																			select: monthSelectList,
																		}}
																	/>
																	<PayPalHostedField
																		hostedFieldType={PAYPAL_HOSTED_FIELDS_TYPES.EXPIRATION_YEAR}
																		options={{
																			selector: '#expiration-year',
																			placeholder: 'YYYY',
																			select: true,
																		}}
																	/>
																	<PayPalHostedField
																		hostedFieldType={PAYPAL_HOSTED_FIELDS_TYPES.CVV}
																		options={{ selector: '#cvv', placeholder: '123' }}
																	/>
																	<div className={classes.paymentCreditCardInput}>
																		<div className="form-box">
																			<div className="form-head">
																				<label htmlFor="">カード番号 （Card Number）</label>
																				<span className="required">必須</span>
																			</div>
																			<div className="form-input width50">
																				<div id="card-number" className={classes.cardField} />
																			</div>
																		</div>
																		<div className="form-box">
																			<div className="form-head">
																				<label htmlFor="">有効期限 （Expiration Date）</label>
																				<span className="required">必須</span>
																			</div>
																			<div className={classes.paymentCreditExpirationDateRow + ` form-input width50`}>
																				<div className={classes.paymentCreditExpirationDateSelectBox}>
																					<div id="expiration-month" className={classes.cardField} />
																				</div>
																				<span className={classes.paymentCreditExpirationDateSlash}>/</span>
																				<div className={classes.paymentCreditExpirationDateSelectBox}>
																					<div id="expiration-year" className={classes.cardField} />
																				</div>
																			</div>
																		</div>
																		<div className="form-box">
																			<div className="form-head">
																				<label htmlFor="">セキュリティコード （Security Codes）</label>
																				<span className="required">必須</span>
																			</div>
																			<div className="form-input width50">
																				<div id="cvv" className={classes.cardField} />
																			</div>
																		</div>
																		<div className="form-box">
																			<div className="form-input">
																				<div className="check-box">
																					<ul>
																						<li>
																							<label
																								className={`check-label ${isSaveCard ? 'is-active' : ''}`}
																								htmlFor="isSaveCard"
																							>
																								<input type="checkbox" id="isSaveCard" checked={isSaveCard} disabled />
																								カード情報を保存する
																							</label>
																						</li>
																					</ul>
																				</div>
																			</div>
																		</div>
																		<AgreeSendingCardInfo
																			isAgreeSendCardInfo={isAgreeSendCardInfo}
																			handleChangeAgreeCheckValue={handleChangeAgreeCheckValue}
																		/>
																	</div>
																	<div className="btn-box">
																		{paymentTokenExists && (
																			<div className={`btn wh ${classes.fixSizeBtn}`}>
																				<button onClick={() => setPage(Page.SHOW_SAVED_CARD)}>
																					別のカードで支払う
																				</button>
																			</div>
																		)}
																		<SubmitPayment fixSize={paymentTokenExists} />
																	</div>
																</PayPalHostedFieldsProvider>
															)}
															{/* 保存済みカード情報IDがある&新規カード以外が選択されている場合は閲覧のみ */}
															{isCredit(payMethod) && paymentTokenExists && !isCardSelected(NEW_CARD_SELECT_VALUE) && (
																<>
																	<div className={classes.paymentCreditCardInput}>
																		<div className="form-box">
																			<div className="form-head">
																				<label htmlFor="">カード番号 （Card Number）</label>
																			</div>
																			<div className={`${classes.marginLeft20} form-input width50`}>
																				<label htmlFor="">{`**** **** **** ${order.payment_tokens[selectedCard].last_digits}`}</label>
																			</div>
																		</div>
																		<div className="form-box">
																			<div className="form-head">
																				<label htmlFor="">有効期限 （Expiration Date）</label>
																			</div>
																			<div
																				className={`${classes.marginLeft20} ${classes.paymentCreditExpirationDateRow} form-input width50`}
																			>
																				<label htmlFor="">{`${
																					order.payment_tokens[selectedCard].expiry.split('-')[1]
																				} / ${order.payment_tokens[selectedCard].expiry.split('-')[0]}`}</label>
																			</div>
																		</div>
																	</div>
																	<div className="btn-box">
																		<div className={`btn wh ${classes.fixSizeBtn}`}>
																			<button onClick={() => setPage(Page.SHOW_SAVED_CARD)}>別のカードで支払う</button>
																		</div>
																		<div className={`btn gr ${classes.fixSizeBtn}`}>
																			<button onClick={paySavedCreditCard}>このカードで支払う</button>
																		</div>
																	</div>
																</>
															)}
														</div>
													</>
												)}
											</div>
										</div>
										<div className="btn-box">
											<div className="btn wh">
												<button onClick={() => redirect(Payment.BACK)}>戻る</button>
											</div>
										</div>
									</>
								)}
								{isShowSavedCard() && (
									<>
										{/* 別のカードで支払う場合 */}
										<div className={`section-head ${order.logo ? classes.marginTop30 : ''}`}>
											<span>クレジットカード情報</span>
										</div>
										{isPaymentTokenMax && (
											<div className="section-txt-box">
												<div className="txt">
													<div>{CARD_MAX_MESSAGE}</div>
												</div>
											</div>
										)}
										{error !== '' && (
											<div className="error-box">
												<div className="error-box-in">
													<p>{error}</p>
												</div>
											</div>
										)}
										<div className="info-box form-box">
											<div className="form-input">
												<div className="info-box">
													{paymentTokenExists && (
														<>
															<div className={classes.paymentTokenRowHead}>
																<div className={'info-head ' + classes.paymentTokenRowHeadCol} />
																<div className={'info-head ' + classes.paymentTokenRowHeadCol} />
																<div className={'info-head ' + classes.paymentTokenRowHeadCol}>カード番号</div>
																<div className={'info-head ' + classes.paymentTokenRowHeadCol}>有効期限</div>
																<div className={'info-head ' + classes.paymentTokenRowHeadCol} />
															</div>
															{order.payment_tokens.map((t, i) => {
																return (
																	<>
																		<Hr />
																		<div className={classes.paymentTokenRow}>
																			<div className="radio-box">
																				<ul>
																					<li className={classes.paymentTokenLi}>
																						<label
																							className={`radio-label ${classes.paymentTokenLabel} ${
																								isCardSelected(i) ? 'is-active' : ''
																							}`}
																							htmlFor={`after${i}`}
																						>
																							<input
																								type="radio"
																								id={`after${i}`}
																								name={`method${i}`}
																								value={i}
																								checked={isCardSelected(i)}
																								onChange={handleChangeSavedCardSelectValue}
																							/>
																						</label>
																						<div className={classes.paymentTokenCol}>
																							{displayCardBrandImage(t.brand)}
																						</div>
																						<div className={classes.paymentTokenCol}>
																							{`**** **** **** ${t.last_digits}`}
																						</div>
																						<div className={classes.paymentTokenCol}>{`${t.expiry.split('-')[1]} / ${
																							t.expiry.split('-')[0]
																						}`}</div>
																						<div
																							className={classes.savedCardDeleteButton}
																							onClick={() => selectDeleteCard(i)}
																						>
																							削除
																						</div>
																					</li>
																				</ul>
																			</div>
																		</div>
																	</>
																);
															})}
														</>
													)}
													{/* カード保存上限に達していなかったら新規カード入力選択肢を表示する */}
													{!isPaymentTokenMax && (
														<>
															<Hr />
															<div className={classes.sectionPaymentInfoRow}>
																<div className={classes.paymentCreditHead}>
																	<div className="radio-box">
																		<ul>
																			<li>
																				<label
																					className={`radio-label ${classes.paymentMethodLabel} ${
																						isCardSelected(NEW_CARD_SELECT_VALUE) ? 'is-active' : ''
																					}`}
																					htmlFor="credit"
																				>
																					<input
																						type="radio"
																						id="credit"
																						name={`method${NEW_CARD_SELECT_VALUE}`}
																						value={NEW_CARD_SELECT_VALUE}
																						checked={isCardSelected(NEW_CARD_SELECT_VALUE)}
																						onChange={handleChangeSavedCardSelectValue}
																					/>
																					新規クレジットカード
																				</label>
																			</li>
																		</ul>
																	</div>
																	<div className={`${classes.paymentCreditCardBox} ${classes.flexCenter}`}>
																		{displayCardBrandImage()}
																	</div>
																</div>
																<PayPalHostedFieldsProvider
																	// 次へ押下時にsubmitの前に発火
																	createOrder={createOrder}
																	// 資格情報が無い場合に発火
																	notEligibleError={<div>Your account is not eligible</div>}
																>
																	<PayPalHostedField
																		hostedFieldType={PAYPAL_HOSTED_FIELDS_TYPES.NUMBER}
																		options={{ selector: '#card-number', placeholder: '********' }}
																	/>
																	<PayPalHostedField
																		hostedFieldType={PAYPAL_HOSTED_FIELDS_TYPES.EXPIRATION_MONTH}
																		options={{
																			selector: '#expiration-month',
																			placeholder: 'MM',
																			select: monthSelectList,
																		}}
																	/>
																	<PayPalHostedField
																		hostedFieldType={PAYPAL_HOSTED_FIELDS_TYPES.EXPIRATION_YEAR}
																		options={{
																			selector: '#expiration-year',
																			placeholder: 'YYYY',
																			select: true,
																		}}
																	/>
																	<PayPalHostedField
																		hostedFieldType={PAYPAL_HOSTED_FIELDS_TYPES.CVV}
																		options={{ selector: '#cvv', placeholder: '123' }}
																	/>
																	<div className={classes.paymentCreditCardInput}>
																		<div className="form-box">
																			<div className="form-head">
																				<label htmlFor="">カード番号 （Card Number）</label>
																				<span className="required">必須</span>
																			</div>
																			<div className="form-input width50">
																				<div id="card-number" className={classes.cardField} />
																			</div>
																		</div>
																		<div className="form-box">
																			<div className="form-head">
																				<label htmlFor="">有効期限 （Expiration Date）</label>
																				<span className="required">必須</span>
																			</div>
																			<div className={classes.paymentCreditExpirationDateRow + ` form-input width50`}>
																				<div className={classes.paymentCreditExpirationDateSelectBox}>
																					<div id="expiration-month" className={classes.cardField} />
																				</div>
																				<span className={classes.paymentCreditExpirationDateSlash}>/</span>
																				<div className={classes.paymentCreditExpirationDateSelectBox}>
																					<div id="expiration-year" className={classes.cardField} />
																				</div>
																			</div>
																		</div>
																		<div className="form-box">
																			<div className="form-head">
																				<label htmlFor="">セキュリティコード （Security Codes）</label>
																				<span className="required">必須</span>
																			</div>
																			<div className="form-input width50">
																				<div id="cvv" className={classes.cardField} />
																			</div>
																		</div>
																		<div className="form-box">
																			<div className="form-input">
																				<div className="check-box">
																					<ul>
																						<li>
																							<label
																								className={`check-label ${isSaveCard ? 'is-active' : ''}`}
																								htmlFor="isSaveCard"
																							>
																								<input type="checkbox" id="isSaveCard" checked={isSaveCard} disabled />
																								カード情報を保存する
																							</label>
																						</li>
																					</ul>
																				</div>
																			</div>
																		</div>
																		<AgreeSendingCardInfo
																			isAgreeSendCardInfo={isAgreeSendCardInfo}
																			handleChangeAgreeCheckValue={handleChangeAgreeCheckValue}
																		/>
																	</div>
																	{isCardSelected(NEW_CARD_SELECT_VALUE) && (
																		<>
																			<Hr />
																			<div className="btn-box">
																				<div className="btn wh">
																					<button onClick={() => setPage(Page.DEFAULT)}>戻る</button>
																				</div>
																				<SubmitPayment label="このカードで支払う" />
																			</div>
																		</>
																	)}
																</PayPalHostedFieldsProvider>
															</div>
														</>
													)}
												</div>
												{!isCardSelected(NEW_CARD_SELECT_VALUE) && (
													<>
														<Hr />
														<div className={classes.sectionPaymentInfoRow}>
															<div className="btn-box">
																<div className="btn wh">
																	<button onClick={() => setPage(Page.DEFAULT)}>戻る</button>
																</div>
																<div className="btn gr">
																	<button onClick={paySavedCreditCard}>このカードで支払う</button>
																</div>
															</div>
														</div>
													</>
												)}
											</div>
										</div>
									</>
								)}
								{isDeleteCard() && paymentTokenExists && <DeleteCardComponent />}
								{isConfirm() && (
									<ConfirmComponent
										Overview={<BulkPeriodicPaymentOverview isConfirmPage />}
										order={order}
										payMethod={payMethod}
										transErrorPage={transErrorPage}
										setPage={setPage}
										displayCardBrandImage={displayCardBrandImage}
										setConfirmLoading={setConfirmLoading}
										is_via_portal={is_via_portal}
										redirect={redirect}
										site={site}
										siteSettlement={siteSettlement}
										redirect_uri={redirect_uri}
										state={state}
										completeUrl={RoutePath.BULK_PERIODIC_PAYMENT_COMPLETE}
										setCardError={setCardError}
									/>
								)}
							</div>
						</>
					)}
				</div>
			)}
		</>
	);
});
